import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import Select from "react-select";
import debounce from "lodash/debounce";
import { SelectOption, AsyncSelectLoadResult } from "./types";

export interface InfiniteScrollSelectProps {
  options: SelectOption[];
  value: SelectOption | null;
  onChange: (selected: SelectOption | null) => void;
  onInputChange?: (value: string, actionMeta: any) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  placeholder?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  className?: string;
  classNamePrefix?: string;
  isLoading?: boolean;
  isMulti?: boolean;
  noOptionsMessage?: () => string;
  formatOptionLabel?: any;
}

const InfiniteScrollSelect: React.FC<InfiniteScrollSelectProps> = ({
  options,
  value,
  onChange,
  onInputChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isLoading = false,
  placeholder = "Select...",
  isClearable = false,
  isDisabled = false,
  className = "select",
  classNamePrefix = "react-select",
  isMulti = false,
  noOptionsMessage = () => "No options found",
  formatOptionLabel,
}) => {
  const selectRef = useRef<any>(null);

  const handleMenuScrollToBottom = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  const customNoOptionsMessage = () => {
    if (isLoadingMore) {
      return "Loading more options...";
    }
    return noOptionsMessage();
  };

  const customOptions = [
    ...options,
    ...(isLoadingMore
      ? [
          {
            value: -1,
            label: "Loading more...",
            isDisabled: true,
          },
        ]
      : []),
  ];

  return (
    <Select
      ref={selectRef}
      options={customOptions}
      value={value as any}
      onChange={onChange as any}
      onInputChange={onInputChange}
      isLoading={isLoading}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      className={className}
      classNamePrefix={classNamePrefix}
      isMulti={isMulti}
      noOptionsMessage={customNoOptionsMessage}
      onMenuScrollToBottom={handleMenuScrollToBottom}
      menuPlacement="auto"
      menuShouldScrollIntoView={false}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export interface PaginatedAsyncSelectProps {
  loadOptions: (page: number, search: string) => Promise<AsyncSelectLoadResult>;
  value: SelectOption | null;
  onChange: (selected: SelectOption | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  isMulti?: boolean;
  className?: string;
  classNamePrefix?: string;
  noOptionsMessage?: () => string;
  formatOptionLabel?: any;
}

const PaginatedAsyncSelect: React.FC<PaginatedAsyncSelectProps> = ({
  loadOptions,
  value,
  onChange,
  placeholder = "Select...",
  isClearable = false,
  isDisabled = false,
  isMulti = false,
  className = "select",
  classNamePrefix = "react-select",
  noOptionsMessage,
  formatOptionLabel,
}) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const loadOptionsRef = useRef(loadOptions);
  useEffect(() => {
    loadOptionsRef.current = loadOptions;
  }, [loadOptions]);

  const fetchOptions = useCallback(
    async (page: number, append: boolean, search: string) => {
      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }

        const result = await loadOptionsRef.current(page, search);

        if (append) {
          setOptions((prev) => [...prev, ...result.options]);
        } else {
          setOptions(result.options);
        }

        setCurrentPage(page);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error("Error loading options:", err);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchOptions(1, false, "");
  }, [fetchOptions]);

  const debouncedFetchOptions = useMemo(
    () =>
      debounce((search: string) => {
        fetchOptions(1, false, search);
      }, 2000),
    [fetchOptions]
  );

  const handleInputChange = (inputValue: string, actionMeta: any) => {
    if (actionMeta && actionMeta.action !== "input-change") {
      return;
    }
    setSearchValue(inputValue);
    debouncedFetchOptions(inputValue);
  };

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      fetchOptions(currentPage + 1, true, searchValue);
    }
  }, [hasMore, isLoadingMore, currentPage, searchValue, fetchOptions]);

  return (
    <InfiniteScrollSelect
      options={options}
      value={value}
      onChange={onChange}
      onInputChange={handleInputChange}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      isLoading={isLoading}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMulti={isMulti}
      className={className}
      classNamePrefix={classNamePrefix}
      noOptionsMessage={noOptionsMessage}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export { InfiniteScrollSelect, PaginatedAsyncSelect };
export default InfiniteScrollSelect;
